import { pdf } from '@react-pdf/renderer';
import * as stylex from '@stylexjs/stylex';
import { useMutation } from '@tanstack/react-query';
import { useQuery } from '@tanstack/react-query-latest';
import { createFileRoute, redirect } from '@tanstack/react-router';
import fileDownload from 'js-file-download';
import type { ReactElement } from 'react';

import { SecondaryActionsIconButton } from '@/components/secondary-actions-icon-button.component';
import {
  Badge,
  Breadcrumb,
  Breadcrumbs,
  Download01StrokeIcon,
  Heading,
  MenuItem,
  Paragraph,
  Skeleton,
  Text,
  View,
  type BadgeProps,
} from '@/libs/serenity-ui';
import { $ } from '@/libs/serenity-ui/tokens.stylex';
import { RouterLink } from '@/libs/tanstack-router';
import { organizationUserApiKeyMutations, organizationUserApiKeyQueries } from '@/resources/organization-user-api-key/organization-user-api-key.api';
import { CodeSnippet } from '@/routes/_main/_details/api-keys/guide/-components/code-snippet.component';
import { GuidePdfDocument } from '@/routes/_main/_details/api-keys/guide/-components/guide-pdf-document.component';

export const Route = createFileRoute('/_main/_details/api-keys/guide/')({
  component: RouteComponent,
  beforeLoad: ({ context }) => {
    if (context.user && !context.can.apiKeys.view()) {
      throw redirect({ to: '/' });
    }
  },
  loader: ({ context: { queryClient }}) => {
    queryClient.prefetchQuery(organizationUserApiKeyQueries.integrationSchema());
  },
  staticData: {
    details: {
      heading: 'Customer Integration API',
      slots: {
        topBarComponent: TopBarComponent,
        actionsComponent: ActionsComponent,
      },
    },
  },
  head: () => ({
    meta: [
      {
        title: 'TrendMD | API Keys — Customer Integration API',
      },
    ],
  }),
});

function RouteComponent(): ReactElement {
  const query = useQuery(organizationUserApiKeyQueries.integrationSchema());

  const examplePath = Object.entries(query.data?.paths ?? {}).find(([ , methods ]) => 'get' in methods)?.[0] ?? '/customer/api/v1/teams?limit=20';

  const postExamplePath = Object.entries(query.data?.paths ?? {}).find(([ , methods ]) => NON_GET_METHODS.some((m) => m in methods))?.[0] ?? '/customer/api/v1/teams';

  const uploadExamplePath = Object.entries(query.data?.paths ?? {}).find(([ , methods ]) =>
    Object.values(methods).some((op) => op?.requestBody?.content !== undefined && 'multipart/form-data' in op.requestBody.content))?.[0] ?? '/customer/api/v1/uploads';

  const endpoints: Array<{ method: string; path: string; summary?: string; description?: string }> = [];
  for (const [ path, pathItem ] of Object.entries(query.data?.paths ?? [])) {
    for (const method of HTTP_METHOD_ORDER) {
      const operation = pathItem[method];
      if (operation) {
        endpoints.push({
          method: method.toUpperCase(),
          path,
          summary: operation.summary,
          description: operation.description,
        });
      }
    }
  }

  return (
    <article {...stylex.props(articleStyles.base)}>
      <section {...stylex.props(sectionStyles.base)}>
        <Heading level={2} size={24} style={bottomSpacingVariantStyles.small} weight="medium">Overview</Heading>
        <Paragraph color="secondary" size={16} style={bottomSpacingVariantStyles.small}>
          The Customer Integration API is the programmatic surface for managing campaigns, teams, files, and other customer
          resources from your own systems. Every request is signed with a secret key issued to your integration, so the
          server can verify both who you are and that the request has not been tampered with in transit.
        </Paragraph>
        <Paragraph color="secondary" size={16}>This page covers everything you need to call the API:</Paragraph>
        <ul {...stylex.props(listStyles.base)}>
          <li {...stylex.props(listStyles.item)}>
            Credentials and the OpenAPI schema
            <span {...stylex.props(listStyles.description)}> — where to get them.</span>
          </li>
          <li {...stylex.props(listStyles.item)}>
            Authentication
            <span {...stylex.props(listStyles.description)}> — how to sign requests so the server accepts them.</span>
          </li>
          <li {...stylex.props(listStyles.item)}>
            Errors
            <span {...stylex.props(listStyles.description)}> — what to do when a request is declined.</span>
          </li>
        </ul>
      </section>

      <section {...stylex.props(sectionStyles.base)}>
        <Heading level={2} size={24} style={bottomSpacingVariantStyles.small} weight="medium">Getting started</Heading>

        <section {...stylex.props(sectionStyles.base, bottomSpacingVariantStyles.small)}>
          <Heading level={3} size={20} weight="medium">1. Generate an API key</Heading>
          <Paragraph color="secondary" size={16}>
            Open the customer dashboard and navigate to{' '}
            <strong {...stylex.props(textStyles.primary)}>Settings → API Keys</strong>{' '}
            and click the <strong {...stylex.props(textStyles.primary)}>+ Create API key</strong> button.
            You will receive two values:
          </Paragraph>
          <ul {...stylex.props(listStyles.base)}>
            <li {...stylex.props(listStyles.item)}>
              Api Key
              <span {...stylex.props(listStyles.description)}>
                {' '}— a public identifier with the prefix tmdc_ak_. Goes in the X-Api-Key header on every request.
              </span>
            </li>
            <li {...stylex.props(listStyles.item)}>
              Secret key
              <span {...stylex.props(listStyles.description)}>
                {' '}— with the prefix tmdc_sk_. <strong {...stylex.props(textStyles.primary)}>Shown only once.</strong>{' '}
                Store it in a secret manager (Vault, AWS Secrets Manager, 1Password, etc.) — you cannot recover it later.
              </span>
            </li>
          </ul>
          <Paragraph color="secondary" size={16}>
            Both values are tied to a specific organization and user. Revoking the key in the dashboard invalidates it immediately.
          </Paragraph>
        </section>

        <section {...stylex.props(sectionStyles.base)}>
          <Heading level={3} size={20} weight="medium">2. Download the OpenAPI schema</Heading>
          <Paragraph color="secondary" size={16}>
            The full endpoint catalog is published as an OpenAPI 3.x JSON document. Open it in any OpenAPI viewer
            (Swagger UI, Redoc, Stoplight) or load it into your code-generation tool of choice to scaffold a typed client.
          </Paragraph>
        </section>
      </section>

      <section {...stylex.props(sectionStyles.base)}>
        <Heading level={2} size={24} style={bottomSpacingVariantStyles.small} weight="medium">Authentication</Heading>
        <Paragraph color="secondary" size={16} style={bottomSpacingVariantStyles.small}>
          Every request must include three headers. Together they identify your integration, prove the request has not been
          altered in transit, and bound the request to a small time window so a captured request cannot be replayed later.
        </Paragraph>

        <section {...stylex.props([ sectionStyles.base, bottomSpacingVariantStyles.small ])}>
          <Heading level={3} size={20} weight="medium">Required headers</Heading>
          <Paragraph color="secondary" size={16} style={bottomSpacingVariantStyles.small}>Every request to the Customer Integration API must include all three of these headers.</Paragraph>

          <View {...stylex.props(tableStyles.wrapper)}>
            <table {...stylex.props(tableStyles.table)}>
              <thead>
                <tr>
                  <th {...stylex.props(tableStyles.th)}>Header</th>
                  <th {...stylex.props(tableStyles.th)}>Format</th>
                  <th {...stylex.props(tableStyles.th)}>Example</th>
                  <th {...stylex.props(tableStyles.th)}>Purpose</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td {...stylex.props(tableStyles.td, tableStyles.nonBreakableTd)}>X-Api-Key</td>
                  <td {...stylex.props(tableStyles.td)}>string, prefix tmdc_ak_</td>
                  <td {...stylex.props(tableStyles.td, tableStyles.breakableTd)}>tmdc_ak_4f3e2d1c0b9a87654321</td>
                  <td {...stylex.props(tableStyles.td)}>Identifies the integration making the request.</td>
                </tr>
                <tr>
                  <td {...stylex.props(tableStyles.td, tableStyles.nonBreakableTd)}>X-Timestamp</td>
                  <td {...stylex.props(tableStyles.td)}>UNIX timestamp, seconds (int64)</td>
                  <td {...stylex.props(tableStyles.td, tableStyles.breakableTd)}>1713321600</td>
                  <td {...stylex.props(tableStyles.td)}>When the request was generated. Must be within ±5 minutes of the server&apos;s clock.</td>
                </tr>
                <tr>
                  <td {...stylex.props(tableStyles.td, tableStyles.nonBreakableTd)}>X-Signature</td>
                  <td {...stylex.props(tableStyles.td)}>string, prefix sha256=, then hex</td>
                  <td {...stylex.props(tableStyles.td)}>sha256=9a3f7c1e...</td>
                  <td {...stylex.props(tableStyles.td)}>HMAC-SHA256 of the canonical string, signed with your secret key.</td>
                </tr>
              </tbody>
            </table>
          </View>
        </section>

        <section {...stylex.props(sectionStyles.base)}>
          <Heading level={3} size={20} weight="medium">Computing the signature</Heading>
          <Paragraph color="secondary" size={16}>Signing is a three-step process:</Paragraph>

          <ol {...stylex.props(listStyles.base, bottomSpacingVariantStyles.small)}>
            <li {...stylex.props(listStyles.item)}>
              Build the canonical string{' '}
              <span {...stylex.props(listStyles.description)}>
                from the request — a small text representation derived from method, path, timestamp, and body.
              </span>
            </li>
            <li {...stylex.props(listStyles.item)}>
              HMAC-SHA256
              <span {...stylex.props(listStyles.description)}>{' '}that string with your secret key.</span>
            </li>
            <li {...stylex.props(listStyles.item)}>
              Hex-encode the digest and prepend sha256=
              <span {...stylex.props(listStyles.description)}>. Send the result as X-Signature.</span>
            </li>
          </ol>

          <Paragraph color="secondary" size={16} style={bottomSpacingVariantStyles.small}>
          The canonical string layout depends on the request type. There are three variants.
          </Paragraph>

          <section {...stylex.props(sectionStyles.base, bottomSpacingVariantStyles.medium)}>
            <Heading level={4} size={20} weight="medium">Variant A — GET (no body)</Heading>
            <CodeSnippet>{`METHOD\\n\nPATH\\n\nTIMESTAMP`}</CodeSnippet>
            <Paragraph color="secondary" size={16} style={bottomSpacingVariantStyles.small}>Three lines. No body component.</Paragraph>

            <Paragraph size={16} weight="semibold">Example:</Paragraph>
            <CodeSnippet isPending={query.isPending}>{`GET\n${examplePath}\n1713321600`}</CodeSnippet>
          </section>

          <section {...stylex.props(sectionStyles.base, bottomSpacingVariantStyles.medium)}>
            <Heading level={4} size={20} weight="medium">Variant B — Non-GET request with a JSON or form-urlencoded body</Heading>
            <CodeSnippet>{`METHOD\\n\nPATH\\n\nTIMESTAMP\\n\nRAW_BODY`}</CodeSnippet>
            <Paragraph color="secondary" size={16} style={bottomSpacingVariantStyles.small}>
              Four lines. The fourth line is the request body{' '}
              <strong {...stylex.props(textStyles.primary)}>byte-for-byte exactly as transmitted</strong>.
              Sign the final serialized body — do not re-serialize, re-format, or re-order keys after computing the signature, or it will not match.
            </Paragraph>

            <Paragraph size={16} weight="semibold">Example:</Paragraph>
            <CodeSnippet isPending={query.isPending}>{`POST\n${postExamplePath}\n1713321600\n{"name":"Acme Inc","description":"Pilot account"}`}</CodeSnippet>
          </section>

          <section {...stylex.props(sectionStyles.base, bottomSpacingVariantStyles.medium)}>
            <Heading level={4} size={20} weight="medium">Variant C — Multipart request with a file upload</Heading>
            <CodeSnippet>{`METHOD\\n\nPATH\\n\nTIMESTAMP\\n\nSHA256_HEX(file_bytes)`}</CodeSnippet>
            <Paragraph color="secondary" size={16} style={bottomSpacingVariantStyles.small}>
              Four lines. The fourth line is the{' '}
              <strong {...stylex.props(textStyles.primary)}>lowercase hex SHA-256 digest</strong>{' '}
              of the file&apos;s raw bytes — not the multipart envelope, not a base64 encoding, not the form-field metadata.
            </Paragraph>

            <Paragraph size={16} weight="semibold">Example:</Paragraph>
            <CodeSnippet isPending={query.isPending}>{`POST\n${uploadExamplePath}\n1713321600\nb94d27b9934d3e08a52e52d7da7dabfac484efe37a5380ee9088f7ace2efcde9`}</CodeSnippet>
          </section>

          <View style={contextParagraphStyles.base}>
            <Paragraph size={16} weight="semibold">Notes that apply to all three variants:</Paragraph>
            <ul {...stylex.props(listStyles.base)}>
              <li {...stylex.props(listStyles.item)}>
                PATH
                <span {...stylex.props(listStyles.description)}>
                  {' '}is the full request path including query string. GET /teams?limit=20 and GET /teams produce different
                  canonical strings — the path you sign must match the path you send.
                </span>
              </li>
              <li {...stylex.props(listStyles.item)}>
                METHOD
                <span {...stylex.props(listStyles.description)}>{' '}is uppercase: GET, POST, PUT, PATCH, DELETE.</span>
              </li>
              <li {...stylex.props(listStyles.item)}>
                Line separator
                <span {...stylex.props(listStyles.description)}>
                  {' '}is a single newline (\n, 0x0A). No carriage returns. No trailing newline.
                </span>
              </li>
            </ul>
          </View>
        </section>
      </section>

      <section {...stylex.props(sectionStyles.base)}>
        <Heading level={3} size={20} weight="medium">Code sample (Node.js)</Heading>
        <CodeSnippet isPending={query.isPending} style={bottomSpacingVariantStyles.medium}>
          {`const crypto = require('node:crypto');

function sign({ method, path, timestamp, body, secretKey }) {
  let canonical;
  if (method === 'GET') {
    canonical = \`\${method}\\n\${path}\\n\${timestamp}\`;
  } else {
    canonical = \`\${method}\\n\${path}\\n\${timestamp}\\n\${body}\`;
  }
  const digest = crypto.createHmac('sha256', secretKey).update(canonical).digest('hex');
  return \`sha256=\${digest}\`;
}

const timestamp = Math.floor(Date.now() / 1000);

const headers = {
  'X-Api-Key': 'tmdc_ak_...',
  'X-Timestamp': timestamp.toString(),
  'X-Signature': sign({
    method: 'POST',
    path: '${postExamplePath}',
    timestamp,
    body: JSON.stringify({ name: 'Acme Inc' }),
    secretKey: 'tmdc_sk_...',
  }),
};`}
        </CodeSnippet>

        <Paragraph color="secondary" size={16}>
          <strong {...stylex.props(textStyles.primary)}>Multipart upload variant</strong>{' '}
          — replace body with the hex SHA-256 digest of the file&apos;s raw bytes:
        </Paragraph>
        <CodeSnippet isPending={query.isPending}>
          {`const fileHash = crypto.createHash('sha256').update(fileBuffer).digest('hex');

const signature = sign({
  method: 'POST',
  path: '${uploadExamplePath}',
  timestamp,
  body: fileHash,
  secretKey: 'tmdc_sk_...',
});`}
        </CodeSnippet>
      </section>

      <section {...stylex.props(sectionStyles.base)}>
        <Heading level={2} size={24} style={bottomSpacingVariantStyles.small} weight="medium">Available endpoints</Heading>
        <Paragraph color="secondary" size={16} style={bottomSpacingVariantStyles.small}>All endpoints share the same authentication scheme described above.</Paragraph>

        {(query.isPending || endpoints.length > 0) &&
          <View {...stylex.props(tableStyles.wrapper)}>
            <table {...stylex.props(tableStyles.table)}>
              <thead>
                <tr>
                  <th {...stylex.props(tableStyles.th)}>Method</th>
                  <th {...stylex.props(tableStyles.th)}>Path</th>
                  <th {...stylex.props(tableStyles.th)}>Description</th>
                </tr>
              </thead>
              <tbody>
                {query.isPending ?
                  Array.from({ length: SKELETON_ENDPOINT_ROW_COUNT }, (_, i) => (
                    <tr key={`skeleton-${i}`}>
                      <td {...stylex.props(tableStyles.td)}>
                        <Skeleton style={[ skeletonEndpointStyles.base, skeletonEndpointStyles.method ]} />
                      </td>
                      <td {...stylex.props(tableStyles.td)}>
                        <Skeleton style={[ skeletonEndpointStyles.base, skeletonEndpointStyles.path ]} />
                      </td>
                      <td {...stylex.props(tableStyles.td)}>
                        <Skeleton style={[ skeletonEndpointStyles.base, skeletonEndpointStyles.description ]} />
                      </td>
                    </tr>
                  )) :
                  endpoints.map(({ method, path, summary, description }) => (
                    <tr key={`${method}-${path}`}>
                      <td {...stylex.props(tableStyles.td)}>
                        <Badge
                          color={HTTP_METHOD_BADGE_COLOR[method] ?? 'greyDark'}
                          isPastel
                          size={20}>
                          {method}
                        </Badge>
                      </td>
                      <td {...stylex.props(tableStyles.td)}>{path}</td>
                      <td {...stylex.props(tableStyles.td)}>{summary ?? description}</td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </View>}
      </section>

      <section {...stylex.props(sectionStyles.base)}>
        <Heading level={2} size={24} style={bottomSpacingVariantStyles.small} weight="medium">Errors</Heading>

        <section {...stylex.props(sectionStyles.base, bottomSpacingVariantStyles.medium)}>
          <Heading level={3} size={20} style={bottomSpacingVariantStyles.small} weight="medium">Quick reference</Heading>
          <View {...stylex.props(tableStyles.wrapper)}>
            <table {...stylex.props(tableStyles.table)}>
              <thead>
                <tr>
                  <th {...stylex.props(tableStyles.th)}>Status</th>
                  <th {...stylex.props(tableStyles.th)}>Meaning</th>
                  <th {...stylex.props(tableStyles.th)}>First thing to check</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td {...stylex.props(tableStyles.td)}>400</td>
                  <td {...stylex.props(tableStyles.td)}>Missing or malformed auth header</td>
                  <td {...stylex.props(tableStyles.td)}>
                    All three headers (X-Api-Key, X-Timestamp, X-Signature) present? Timestamp is an integer in seconds, not milliseconds?
                  </td>
                </tr>
                <tr>
                  <td {...stylex.props(tableStyles.td)}>400</td>
                  <td {...stylex.props(tableStyles.td)}>Timestamp outside ±5 minute window</td>
                  <td {...stylex.props(tableStyles.td)}>Client clock drift. Sync to NTP.</td>
                </tr>
                <tr>
                  <td {...stylex.props(tableStyles.td)}>400</td>
                  <td {...stylex.props(tableStyles.td)}>Invalid signature</td>
                  <td {...stylex.props(tableStyles.td)}>
                    Canonical string mismatch — most often path missing the query string, body re-serialized after signing, or wrong newline.
                  </td>
                </tr>
                <tr>
                  <td {...stylex.props(tableStyles.td)}>403</td>
                  <td {...stylex.props(tableStyles.td)}>API key unknown / revoked / inactive</td>
                  <td {...stylex.props(tableStyles.td)}>Was the key revoked, expired, or never activated?</td>
                </tr>
                <tr>
                  <td {...stylex.props(tableStyles.td)}>403</td>
                  <td {...stylex.props(tableStyles.td)}>Organization or user not active</td>
                  <td {...stylex.props(tableStyles.td)}>Account status issue — contact your account manager.</td>
                </tr>
                <tr>
                  <td {...stylex.props(tableStyles.td)}>403</td>
                  <td {...stylex.props(tableStyles.td)}>Secret key cannot verify the signature</td>
                  <td {...stylex.props(tableStyles.td)}>
                    Secret key out of sync with the API key (likely rotated on the server side, or you are signing with a key from a different integration).
                  </td>
                </tr>
              </tbody>
            </table>
          </View>
        </section>

        <section {...stylex.props(sectionStyles.base)}>
          <Heading level={3} size={20} style={bottomSpacingVariantStyles.small} weight="medium">Common pitfalls</Heading>
          <ul {...stylex.props(listStyles.base)}>
            <li {...stylex.props(listStyles.item)}>
              Body must be byte-identical.
              <span {...stylex.props(listStyles.description)}>
                {' '}For non-GET, non-multipart requests, the body in the canonical string is the exact bytes transmitted.
                If your HTTP client re-serializes JSON (re-orders keys, normalizes whitespace, changes number formatting) after you have signed,
                the signature will not match. Sign the final serialized body.
              </span>
            </li>
            <li {...stylex.props(listStyles.item)}>
              Path must include the query string.
              <span {...stylex.props(listStyles.description)}>
                {' '}GET /teams?limit=20 and GET /teams produce different canonical strings. The path you sign must match what your HTTP client actually sends.
              </span>
            </li>
            <li {...stylex.props(listStyles.item)}>
              Timestamp is seconds, not milliseconds.
              <span {...stylex.props(listStyles.description)}>{' '}Math.floor(Date.now() / 1000) — never Date.now().</span>
            </li>
            <li {...stylex.props(listStyles.item)}>
              Hash the file bytes, not the multipart envelope.
              <span {...stylex.props(listStyles.description)}>
                {' '}For uploads, compute SHA-256 over the raw file content only.
                Do not include multipart boundaries, part headers, or surrounding form fields.
              </span>
            </li>
            <li {...stylex.props(listStyles.item)}>
              Do not hex-encode the file before hashing.
              <span {...stylex.props(listStyles.description)}>
                {' '}The fourth line is SHA256(file_bytes) → hex. Hash the raw bytes, then hex-encode the digest.
                Hex-encoding the file first and then hashing produces a different digest.
              </span>
            </li>
          </ul>
        </section>
      </section>
    </article>
  );
}

function TopBarComponent(): ReactElement {
  return (
    <Breadcrumbs>
      <Breadcrumb>
        <RouterLink to="/api-keys">API keys</RouterLink>
      </Breadcrumb>
      <Breadcrumb hidden>
        <RouterLink to="/api-keys/guide">Customer Integration API</RouterLink>
      </Breadcrumb>
    </Breadcrumbs>
  );
}

function ActionsComponent(): ReactElement {
  const downloadSchemaMutation = useMutation({
    ...organizationUserApiKeyMutations.downloadSchema,
    onSuccess: (data) => fileDownload(data, 'integration-schema.json'),
  });

  const query = useQuery(organizationUserApiKeyQueries.integrationSchema());

  const examplePath = Object.entries(query.data?.paths ?? {}).find(([ , methods ]) => 'get' in methods)?.[0] ?? '/customer/api/v1/teams?limit=20';
  const postExamplePath = Object.entries(query.data?.paths ?? {}).find(([ , methods ]) => NON_GET_METHODS.some((m) => m in methods))?.[0] ?? '/customer/api/v1/teams';
  const uploadExamplePath = Object.entries(query.data?.paths ?? {}).find(([ , methods ]) =>
    Object.values(methods).some((op) => op?.requestBody?.content !== undefined && 'multipart/form-data' in op.requestBody.content))?.[0] ?? '/customer/api/v1/uploads';

  const endpoints: Array<{ method: string; path: string; summary?: string; description?: string }> = [];
  for (const [ path, pathItem ] of Object.entries(query.data?.paths ?? [])) {
    for (const method of HTTP_METHOD_ORDER) {
      const operation = pathItem[method];
      if (operation) {
        endpoints.push({ method: method.toUpperCase(), path, summary: operation.summary, description: operation.description });
      }
    }
  }

  return (
    <SecondaryActionsIconButton>
      <MenuItem onAction={async () => {
        const blob = await pdf(
          <GuidePdfDocument
            endpoints={endpoints}
            examplePath={examplePath}
            postExamplePath={postExamplePath}
            uploadExamplePath={uploadExamplePath}
          />,
        ).toBlob();

        fileDownload(blob, 'trendmd-customer-integration-api.pdf');
      }}>
        <Download01StrokeIcon />
        <Text>Download guide</Text>
      </MenuItem>
      <MenuItem onAction={() => downloadSchemaMutation.mutate()}>
        <Download01StrokeIcon />
        <Text>Download API schema</Text>
      </MenuItem>
    </SecondaryActionsIconButton>
  );
}

const NON_GET_METHODS = [ 'post', 'put', 'patch', 'delete' ] as const;
const HTTP_METHOD_ORDER = [ 'get', 'post', 'put', 'patch', 'delete' ];
const SKELETON_ENDPOINT_ROW_COUNT = 3;

const HTTP_METHOD_BADGE_COLOR: Record<string, BadgeProps['color']> = {
  GET: 'green',
  POST: 'blueDark',
  PUT: 'orange',
  PATCH: 'yellow',
  DELETE: 'red',
};

const bottomSpacingVariantStyles = stylex.create({
  small: {
    marginBottom: 28,
  },
  medium: {
    marginBottom: 56,
  },
});

const articleStyles = stylex.create({
  base: {
    display: 'flex',
    flexDirection: 'column',
    gap: 88,
    justifySelf: 'center',
    maxWidth: 728,
    minWidth: 0,
    width: '100%',
  },
});

const sectionStyles = stylex.create({
  base: {
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
  },
});

const contextParagraphStyles = stylex.create({
  base: {
    alignItems: 'flex-start',
    alignSelf: 'stretch',
    borderLeftColor: $.borderBlack,
    borderLeftStyle: 'solid',
    borderLeftWidth: 2,
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
    justifyContent: 'center',
    paddingLeft: 24,
  },
});

const listStyles = stylex.create({
  base: {
    margin: 0,
    paddingLeft: 20,
  },
  item: {
    color: $.textPrimary,
    fontFamily: 'Inter',
    fontFeatureSettings: "'liga' off, 'clig' off",
    fontSize: 16,
    fontWeight: 600,
    letterSpacing: -0.15,
    lineHeight: '28px',
  },
  description: {
    color: $.textSecondary,
    fontWeight: 400,
  },
});

const textStyles = stylex.create({
  primary: {
    color: $.textPrimary,
  },
});

const skeletonEndpointStyles = stylex.create({
  base: {
    height: 16,
  },
  method: {
    width: 48,
  },
  path: {
    width: '70%',
  },
  description: {
    width: '90%',
  },
});

const tableStyles = stylex.create({
  wrapper: {
    maxWidth: '100%',
    overflowX: 'auto',
    width: '100%',
  },
  table: {
    borderCollapse: 'collapse',
    width: '100%',
  },
  th: {
    borderBottomColor: $.border06,
    borderBottomStyle: 'solid',
    borderBottomWidth: 1,
    color: $.textPrimary,
    fontFamily: 'Inter',
    fontFeatureSettings: "'liga' off, 'clig' off",
    fontSize: 16,
    fontWeight: 600,
    letterSpacing: -0.15,
    lineHeight: '28px',
    paddingBottom: 8,
    paddingInline: 8,
    paddingTop: 0,
    textAlign: 'left',
    verticalAlign: 'top',
    whiteSpace: 'nowrap',
  },
  td: {
    borderBottomColor: $.border10,
    borderBottomStyle: 'solid',
    borderBottomWidth: 1,
    color: $.textSecondary,
    fontFamily: 'Inter',
    fontFeatureSettings: "'liga' off, 'clig' off",
    fontSize: 16,
    fontWeight: 400,
    letterSpacing: -0.15,
    lineHeight: '28px',
    paddingBlock: 12,
    paddingInline: 8,
    verticalAlign: 'top',
  },
  nonBreakableTd: {
    textWrap: 'nowrap',
  },
  breakableTd: {
    wordBreak: 'break-all',
  },
});
